/**
 * Page transitions for TMS 2.0.
 *
 * Every route change inside the app layout animates the content area (`.page-content`):
 *  - "stack": iOS-style push. The new page slides in from the right over the old one, the old
 *    page shifts 28% left and dims; going back reverses it. A swipe from the left edge of the
 *    content area drags the top page back with the pointer.
 *  - "slide": the new page pushes the old one out; going back reverses it.
 *  - "none":  no animation.
 * Style and speed come from Theme Settings (store.state.layout.pageTransition / pageTransitionSpeed).
 *
 * Engines:
 *  - View Transitions API when available: a data-pt-vt attribute on <html> picks the keyframes for
 *    ::view-transition-old/new(tms-page) (see assets/scss/page-transitions.scss).
 *  - Otherwise a "ghost": a static copy of the outgoing page in a fixed, inert layer, animated
 *    against the real incoming page with classes. The ghost is also what the swipe gesture drags.
 *
 * Only transform and opacity animate. The covered page is inert or a snapshot, focus moves to the
 * new page heading, and prefers-reduced-motion: reduce gets a short cross-fade instead.
 * Query-only changes (filters, tabs in the URL) and the first load never animate.
 */
import { nextTick } from 'vue';

const DEFAULT_SPEED = 600;
const STYLES = ['stack', 'slide', 'none'];
const EDGE_SLOP = 6;           // px of horizontal movement before a press on the edge becomes a drag
const FLICK_VELOCITY = 0.6;    // px/ms; a release faster than this completes the swipe
const PROJECTION_MS = 200;     // how far ahead a release velocity is projected

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const historyPosition = () => {
  const p = window.history.state && window.history.state.position;
  return typeof p === 'number' ? p : 0;
};

export function installPageTransitions(router, store) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const root = document.documentElement;
  const supportsVT = typeof document.startViewTransition === 'function';
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const layoutState = () => (store && store.state && store.state.layout) || {};
  const getStyle = () => (STYLES.includes(layoutState().pageTransition) ? layoutState().pageTransition : 'stack');
  const getSpeed = () => {
    const n = Number(layoutState().pageTransitionSpeed);
    return n >= 150 && n <= 1500 ? n : DEFAULT_SPEED;
  };
  const cssNumber = (name, fallback) => {
    const v = parseFloat(getComputedStyle(root).getPropertyValue(name));
    return Number.isFinite(v) ? v : fallback;
  };

  /* ---------------- navigation bookkeeping ---------------- */
  // Vue Router keeps a position counter in history.state: on back/forward it is already the
  // target entry's when guards run, on push it is still the current one.
  let lastPosition = historyPosition();
  let suppressNext = false;  // navigations started by the swipe gesture animate themselves
  let current = null;        // in-flight transition: { skip(), domUpdated() }
  let drag = null;
  const renderWaiters = [];  // resolved once the next navigation has rendered

  const nextRender = () => new Promise((resolve) => renderWaiters.push(resolve));
  const afterRender = async () => {
    await nextTick();
    await nextTick();
    await wait(0); // let the router's scrollBehavior run
  };

  router.beforeResolve((to, from) => {
    if (suppressNext) { suppressNext = false; return true; }
    if (current) current.skip();

    const style = getStyle();
    if (style === 'none' || !from.matched.length || to.path === from.path) return true;
    const oldPage = document.querySelector('.page-content');
    if (!oldPage) return true;

    const dir = historyPosition() < lastPosition ? 'back' : 'forward';
    const mode = reduceQuery.matches ? 'reduce' : dir;
    return supportsVT ? viaViewTransition(oldPage, mode) : viaGhost(oldPage, mode, style);
  });

  router.afterEach((to, from, failure) => {
    if (!failure) lastPosition = historyPosition();
    afterRender().then(() => {
      if (!failure && current && current.domUpdated) current.domUpdated();
      renderWaiters.splice(0).forEach((resolve) => resolve(failure || null));
      syncEdge();
    });
  });

  /* ---------------- focus ---------------- */
  function focusHeading() {
    const page = document.querySelector('.page-content');
    if (!page) return;
    const active = document.activeElement;
    if (active && active !== document.body && page.contains(active)) return; // page set its own focus
    const heading = page.querySelector('.page-title-box h4, h1, h2, h3, h4');
    if (!heading) return;
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.setAttribute('data-tms-pt-focus', '');
    heading.focus({ preventScroll: true });
  }

  /* ---------------- engine A: View Transitions API ---------------- */
  function viaViewTransition(oldPage, mode) {
    return new Promise((proceed) => {
      let markUpdated;
      const updated = new Promise((r) => { markUpdated = r; });
      const oldTop = oldPage.getBoundingClientRect().top;
      let noNewPage = false;

      root.style.setProperty('--tms-pt-dy', '0px');
      root.dataset.ptVt = mode;

      const vt = document.startViewTransition(async () => {
        proceed(true);                                   // let the router commit and render
        await Promise.race([updated, wait(2000)]);
        const page = document.querySelector('.page-content');
        if (!page) { noNewPage = true; return; }
        // keep the outgoing snapshot where the user saw it, whatever the scroll does
        root.style.setProperty('--tms-pt-dy', `${Math.round(oldTop - page.getBoundingClientRect().top)}px`);
      });
      setTimeout(() => proceed(true), 1000);            // never hold navigation hostage

      const entry = { skip: () => vt.skipTransition(), domUpdated: markUpdated };
      current = entry;
      vt.ready.then(() => { if (noNewPage) vt.skipTransition(); }).catch(() => {});
      vt.finished.catch(() => {}).finally(() => {
        if (current === entry) {
          current = null;
          delete root.dataset.ptVt;
          root.style.removeProperty('--tms-pt-dy');
        }
        if (!noNewPage) focusHeading();
      });
    });
  }

  /* ---------------- ghost: static copy of a page in a fixed, inert layer ---------------- */
  function makeGhost(page, { over, drag: isDrag = false }) {
    const rect = page.getBoundingClientRect();
    const wrap = document.createElement('div');
    wrap.className = `tms-pt-ghost ${over ? 'is-over' : 'is-under'}${isDrag ? ' is-drag' : ''}`;
    wrap.setAttribute('aria-hidden', 'true');
    wrap.inert = true;
    wrap.style.left = `${Math.max(0, rect.left)}px`;
    wrap.style.width = `${rect.width}px`;

    const sheet = document.createElement('div');
    sheet.className = 'tms-pt-ghost-page';
    sheet.style.top = `${rect.top}px`;
    sheet.style.minHeight = `${Math.max(rect.height, window.innerHeight - rect.top)}px`; // cover to the bottom of the screen

    const clone = page.cloneNode(true);
    clone.style.viewTransitionName = 'none';
    copyLiveState(page, clone);
    clone.querySelectorAll('[name]').forEach((el) => el.removeAttribute('name')); // keep radio groups apart

    const scrim = document.createElement('div');
    scrim.className = 'tms-pt-ghost-scrim';

    sheet.append(clone, scrim);
    wrap.appendChild(sheet);
    document.body.appendChild(wrap);
    return { wrap, sheet, scrim, width: rect.width };
  }

  function copyLiveState(from, to) {
    const src = from.querySelectorAll('canvas, input, select, textarea');
    const dst = to.querySelectorAll('canvas, input, select, textarea');
    src.forEach((el, i) => {
      const copy = dst[i];
      if (!copy) return;
      try {
        if (el.tagName === 'CANVAS') {
          const ctx = copy.getContext('2d');
          if (ctx && el.width && el.height) ctx.drawImage(el, 0, 0);
        } else if (el.type === 'checkbox' || el.type === 'radio') {
          copy.checked = el.checked;
        } else if ('value' in el && el.type !== 'file') {
          copy.value = el.value;
        }
      } catch (e) { /* WebGL or tainted canvas: leave it blank */ }
    });
  }

  function waitForTransition(el, ms, property) {
    return new Promise((resolve) => {
      let done = false;
      const end = () => {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (e) => { if (e.target === el && (!property || e.propertyName === property)) end(); };
      el.addEventListener('transitionend', onEnd);
      setTimeout(end, ms + 100);
    });
  }

  /* ---------------- engine B: ghost + classes (no View Transitions API) ---------------- */
  function viaGhost(oldPage, mode, style) {
    const over = mode === 'reduce' || (mode === 'back' && style === 'stack');
    const ghost = makeGhost(oldPage, { over });
    root.dataset.ptFallback = mode;
    root.dataset.ptPhase = 'start';

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      ghost.wrap.remove();
      if (current === entry) {
        current = null;
        delete root.dataset.ptFallback;
        delete root.dataset.ptPhase;
      }
      syncEdge();
      focusHeading();
    };
    const run = async () => {
      const page = document.querySelector('.page-content');
      if (!page || finished) { finish(); return; }
      page.getBoundingClientRect();                      // commit the start position
      root.dataset.ptPhase = 'run';
      const target = mode === 'reduce' ? ghost.wrap : (over ? ghost.sheet : page);
      const ms = mode === 'reduce' ? cssNumber('--tms-pt-fade', 160) : getSpeed();
      await waitForTransition(target, ms, mode === 'reduce' ? 'opacity' : 'transform');
      finish();
    };
    const entry = { skip: finish, domUpdated: run };
    current = entry;
    return true;
  }

  /* ---------------- swipe back from the left edge (stack style) ---------------- */
  const edge = document.createElement('div');
  edge.className = 'tms-pt-edge';
  edge.hidden = true;
  edge.setAttribute('aria-hidden', 'true');
  edge.title = 'Drag right to go back';
  document.body.appendChild(edge);

  function canSwipe() {
    return getStyle() === 'stack' && !!(window.history.state && window.history.state.back) &&
      !!document.querySelector('.page-content');
  }
  function syncEdge() {
    const page = document.querySelector('.page-content');
    const ok = canSwipe() && !drag;
    edge.hidden = !ok && !drag;
    // measure the layout column, not the page: the page may be mid-animation (transformed)
    const host = page && (page.closest('.main-content') || page);
    if (ok && host) edge.style.left = `${Math.max(0, host.getBoundingClientRect().left)}px`;
  }
  window.addEventListener('resize', () => syncEdge(), { passive: true });

  const setUnder = (p) => {
    // p: 0 = the top page fully covers, 1 = the top page is gone
    const behind = cssNumber('--tms-pt-behind', -28);
    const dim = cssNumber('--tms-pt-dim', 0.16);
    root.style.setProperty('--tms-pt-under-x', `${behind * (1 - p)}%`);
    root.style.setProperty('--tms-pt-under-dim', `${dim * (1 - p)}`);
  };

  edge.addEventListener('pointerdown', (e) => {
    if (drag || !canSwipe() || (e.pointerType === 'mouse' && e.button !== 0)) return;
    if (current) current.skip();
    edge.setPointerCapture(e.pointerId);
    drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, started: false, samples: [{ x: e.clientX, t: e.timeStamp }] };
  });

  edge.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = Math.max(0, e.clientX - drag.x0);
    if (!drag.started) {
      if (dx < EDGE_SLOP) return;
      beginDrag(drag);
    }
    drag.dx = Math.min(dx, drag.width);
    drag.samples.push({ x: e.clientX, t: e.timeStamp });
    while (drag.samples.length > 2 && e.timeStamp - drag.samples[0].t > 100) drag.samples.shift();
    drag.ghost.sheet.style.transform = `translateX(${drag.dx}px)`;
    setUnder(drag.dx / drag.width);
  });

  function beginDrag(d) {
    const page = document.querySelector('.page-content');
    d.started = true;
    d.ghost = makeGhost(page, { over: true, drag: true });
    d.width = d.ghost.width;
    root.classList.add('tms-pt-dragging');
    setUnder(0);
    d.backDone = nextRender().then((failure) => {
      const under = document.querySelector('.page-content');
      if (under && drag === d) under.inert = true;      // covered page stays out of reach
      return failure;
    });
    suppressNext = true;
    router.back();
  }

  const endDrag = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const d = drag;
    if (!d.started) {
      // a plain click on the edge strip: pass it through to whatever is underneath
      drag = null;
      edge.style.pointerEvents = 'none';
      const target = document.elementFromPoint(e.clientX, e.clientY);
      edge.style.pointerEvents = '';
      if (e.type === 'pointerup' && target && target.click) target.click();
      return;
    }
    const recent = d.samples.concat({ x: e.clientX, t: e.timeStamp }).filter((s) => e.timeStamp - s.t <= 100);
    const v = recent.length > 1
      ? (recent[recent.length - 1].x - recent[0].x) / Math.max(1, recent[recent.length - 1].t - recent[0].t)
      : 0;
    const projected = d.dx + v * PROJECTION_MS;
    const commit = e.type !== 'pointercancel' && (projected > d.width / 2 || v > FLICK_VELOCITY);
    const remaining = commit ? d.width - d.dx : d.dx;
    const speed = getSpeed();
    const ms = reduceQuery.matches ? 0
      : Math.round(Math.min(speed, Math.max(180, ((remaining / d.width) * speed) / Math.max(1, Math.abs(v) * 1.5))));
    settle(d, commit, ms);
  };
  edge.addEventListener('pointerup', endDrag);
  edge.addEventListener('pointercancel', endDrag);

  async function settle(d, commit, ms) {
    root.style.setProperty('--tms-pt-settle', `${ms}ms`);
    root.classList.add('tms-pt-settling');
    d.ghost.sheet.style.transition = `transform ${ms}ms var(--tms-pt-ease)`;
    d.ghost.sheet.getBoundingClientRect();
    d.ghost.sheet.style.transform = `translateX(${commit ? d.width : 0}px)`;
    setUnder(commit ? 1 : 0);
    await waitForTransition(d.ghost.sheet, ms, 'transform');
    const backFailure = await d.backDone;

    if (!commit && !backFailure) {
      // cancelled: return to the page we started on, still hidden behind the ghost
      const forwardDone = nextRender();
      suppressNext = true;
      router.forward();
      await Promise.race([forwardDone, wait(3000)]);
    }
    d.ghost.wrap.remove();
    root.classList.remove('tms-pt-dragging', 'tms-pt-settling');
    ['--tms-pt-under-x', '--tms-pt-under-dim', '--tms-pt-settle'].forEach((p) => root.style.removeProperty(p));
    const page = document.querySelector('.page-content');
    if (page) page.inert = false;
    drag = null;
    syncEdge();
    if (commit) focusHeading();
  }

  /* ---------------- settings -> CSS (last: uses the edge and state above) ---------------- */
  store.watch((s) => s.layout && s.layout.pageTransitionSpeed, () => {
    root.style.setProperty('--tms-pt-dur', `${getSpeed()}ms`);
  }, { immediate: true });
  store.watch((s) => s.layout && s.layout.pageTransition, () => {
    root.dataset.ptStyle = getStyle();
    syncEdge();
  }, { immediate: true });
  store.watch((s) => s.layout && [s.layout.sidebarSize, s.layout.layoutType, s.layout.layoutWidth].join('|'), () => {
    setTimeout(syncEdge, 250); // after the sidebar finishes resizing
  });
}
